import { useMemo } from 'react';

import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import { useStatsigClient } from '../StatsigLib';

/**
 * Hook to evaluate Statsig experiments only for authenticated users
 * and prevent unauthenticated users from being bucketed into experiments
 *
 * @example
 * const rolloutPetCareModes = useAuthenticatedExperiment('ciaf_ssu', 'enable_feature', false);
 */
export const useAuthenticatedExperiment = (
  experimentName: string,
  paramName: string,
  defaultValue = false,
  excludeWhenImpersonating = false
): boolean => {
  const { isAuthenticated, isImpersonating } = useCurrentUser();
  const { client } = useStatsigClient();

  return useMemo(() => {
    if (!isAuthenticated || (excludeWhenImpersonating && isImpersonating)) {
      return defaultValue;
    }

    return Boolean(client.getExperiment(experimentName).get(paramName, defaultValue));
  }, [
    isAuthenticated,
    isImpersonating,
    client,
    experimentName,
    paramName,
    defaultValue,
    excludeWhenImpersonating,
  ]);
};
